// components/patient/OrderList.jsx
import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { orderService } from '../../services/orderService';
import { DB } from '../../utils/db';

export function OrderList({ user, onNavigate }) {
  const { brand } = useBrand();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  
  useEffect(() => {
    loadOrders();
  }, [user.id]);
  
  const mapBackendStatus = (order) => {
    const status = (order.orderStatus || '').toUpperCase();
    if (status === 'CANCELED') return 'canceled';
    if (status === 'COMPLETED' || status === 'CLOSED') {
      const hasDelivery = (order.items || []).some(item =>
        (item.shipments || []).some(s => s.actualDeliveryDate)
      );
      if (hasDelivery || status === 'CLOSED') return 'delivered';
      return 'shipped';
    }
    if (status === 'BACKORDER') return 'backordered';
    if (status === 'OPEN') {
      const hasProcessing = (order.histories || []).some(h =>
        (h.event || '').toLowerCase().includes('process') ||
        (h.message || '').toLowerCase().includes('fulfil')
      );
      return hasProcessing ? 'processing' : 'submitted';
    }
    return 'submitted';
  };

  const mapBackendOrder = (order) => {
    const trackingNumbers = [];
    (order.items || []).forEach(item => {
      (item.shipments || []).forEach(s => {
        if (s.trackingNumber) trackingNumbers.push(s.trackingNumber);
      });
    });
    const timeline = [];
    if (order.startDate) timeline.push({ status: 'submitted', timestamp: order.startDate });
    (order.histories || []).forEach(h => {
      if (h.createdAt) timeline.push({ status: h.status || 'update', timestamp: h.createdAt, note: h.message });
    });

    return {
      id: order.id || 'Unknown',
      status: mapBackendStatus(order),
      createdAt: order.startDate || new Date().toISOString(),
      items: order.items || [],
      itemCount: (order.items || []).length,
      totals: order.totals || {},
      trackingNumber: trackingNumbers[0] || null,
      timeline: timeline.length > 0 ? timeline : [{ status: 'submitted', timestamp: order.startDate || new Date().toISOString() }],
      shippingMethod: order.totals?.shipping > 0 ? 'express' : 'standard',
      _raw: order
    };
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const result = await orderService.getOrders({ pageSize: 50, sortBy: 'startDate', sortAsc: false });
      const hits = result?.hits || [];
      if (hits.length > 0) {
        setOrders(hits.map(mapBackendOrder));
        setUsingFallback(false);
      } else {
        const allOrders = await DB.get('d2p_orders') || [];
        const userOrders = allOrders.filter(o => o.userId === user.id);
        userOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setOrders(userOrders);
        setUsingFallback(true);
      }
    } catch (err) {
      console.warn('Failed to load orders from API, using localStorage fallback:', err.message);
      const allOrders = await DB.get('d2p_orders') || [];
      const userOrders = allOrders.filter(o => o.userId === user.id);
      userOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setOrders(userOrders);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredOrders = filter === 'all'
    ? orders
    : orders.filter(o => o.status === filter);
  
  const filterOptions = [
    { value: 'all', label: 'All Orders', count: orders.length },
    { value: 'submitted', label: 'Submitted', count: orders.filter(o => o.status === 'submitted').length },
    { value: 'processing', label: 'Processing', count: orders.filter(o => o.status === 'processing').length },
    { value: 'shipped', label: 'Shipped', count: orders.filter(o => o.status === 'shipped').length },
    { value: 'delivered', label: 'Delivered', count: orders.filter(o => o.status === 'delivered').length },
    { value: 'backordered', label: 'Backordered', count: orders.filter(o => o.status === 'backordered').length },
    { value: 'canceled', label: 'Canceled', count: orders.filter(o => o.status === 'canceled').length }
  ];
  
  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 32,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              My Orders
            </h1>
            <p style={{ color: '#6B7280' }}>
              Track your medication deliveries
              {usingFallback && (
                <span style={{ color: '#F59E0B', marginLeft: 8, fontSize: 12 }}>(local data)</span>
              )}
            </p>
          </div>
          {!brand.isB2B && (
            <Button icon="package" onClick={() => onNavigate('bundle')}>
              New Order
            </Button>
          )}
        </div>
        
        {/* Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 24,
          borderBottom: '2px solid #E5E7EB',
          overflowX: 'auto'
        }}>
          {filterOptions.map(option => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'transparent',
                borderBottom: filter === option.value ? `3px solid ${brand.c.accent}` : '3px solid transparent',
                marginBottom: -2,
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 14,
                color: filter === option.value ? brand.c.accent : '#6B7280',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
              }}
            >
              {option.label}
              {option.count > 0 && (
                <span style={{
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 12,
                  background: filter === option.value ? brand.c.accent : '#E5E7EB',
                  color: filter === option.value ? 'white' : '#6B7280',
                  fontSize: 12,
                  fontWeight: 700
                }}>
                  {option.count}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 48, height: 48,
                border: '4px solid #E5E7EB',
                borderTop: `4px solid ${brand.c.accent}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: '#6B7280' }}>Loading orders...</p>
            </div>
          </Card>
        )}

        {/* Orders List */}
        {!loading && filteredOrders.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="package" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                {filter === 'all' ? 'No Orders Yet' : `No ${filterOptions.find(f => f.value === filter)?.label}`}
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
                {filter === 'all'
                  ? 'Place your first order to start saving on medications'
                  : `You don't have any orders with this status`
                }
              </p>
              {filter === 'all' && (
                <Button icon="package" onClick={() => onNavigate('bundle')}>
                  Place Your First Order
                </Button>
              )}
            </div>
          </Card>
        ) : !loading && (
          <div style={{ display: 'grid', gap: 16 }}>
            {filteredOrders.map(order => (
              <Card key={order.id} hover onClick={() => onNavigate('order-detail', order.id)}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'start' }}>
                  {/* Icon */}
                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: 12,
                    background: order.status === 'delivered' ? '#D1FAE515' : `${brand.c.accent}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <Icon 
                      name={order.status === 'delivered' ? 'checkCircle' : order.status === 'shipped' ? 'truck' : 'package'} 
                      size={28} 
                      color={order.status === 'delivered' ? '#10B981' : brand.c.accent} 
                    />
                  </div>
                  
                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 8,
                      flexWrap: 'wrap'
                    }}>
                      <h3 style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: brand.c.navy,
                        margin: 0
                      }}>
                        Order {order.id}
                      </h3>
                      <Badge status={order.status} />
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: 12,
                      fontSize: 14,
                      color: '#6B7280',
                      marginBottom: 12
                    }}>
                      <div>
                        <strong style={{ color: '#374151' }}>Items:</strong> {order.itemCount || order.prescriptionIds?.length || (order.items || []).length || 0} item(s)
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Placed:</strong> {new Date(order.createdAt).toLocaleDateString()}
                      </div>
                      <div>
                        <strong style={{ color: '#374151' }}>Shipping:</strong> {order.shippingMethod === 'express' ? 'Express (1-2 days)' : 'Standard (2-3 days)'}
                      </div>
                      {order.trackingNumber && (
                        <div>
                          <strong style={{ color: '#374151' }}>Tracking:</strong> {order.trackingNumber}
                        </div>
                      )}
                    </div>
                    
                    {/* Progress Bar */}
                    {order.timeline && (
                      <div style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 12
                      }}>
                        {['submitted', 'processing', 'shipped', 'delivered'].map((status, i) => {
                          const completed = order.timeline.some(t => t.status === status);
                          return (
                            <div
                              key={status}
                              style={{
                                flex: 1,
                                height: 4,
                                borderRadius: 2,
                                background: completed ? brand.c.accent : '#E5E7EB',
                                transition: 'all 0.3s'
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Arrow */}
                  <Icon name="arrowRight" size={20} color="#9CA3AF" style={{ flexShrink: 0 }} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
